import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";

/**
 * Tiny inline sparkline of price action since a politician's tx_date.
 *
 * Calls GET /api/political-trades/since-trade/{ticker}?tx_date=MM/DD/YYYY
 * and renders a 60×16 SVG polyline + a colored % change pill.
 *
 * Lazy-fetches on first render so the page can render thousands of trade rows
 * without firing thousands of yfinance calls upfront — useful when an entire
 * filer's "Recent disclosed trades" table is expanded.
 */
export default function TradeSinceSparkline({ ticker, txDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let canc = false;
    if (!ticker || !txDate) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(
          `${API}/political-trades/since-trade/${ticker}?tx_date=${encodeURIComponent(
            txDate
          )}`
        );
        if (!canc) setData(r.data);
      } catch {
        if (!canc) setData(null);
      } finally {
        if (!canc) setLoading(false);
      }
    })();
    return () => {
      canc = true;
    };
  }, [ticker, txDate]);

  if (loading) {
    return <div className="w-[60px] h-[16px] bg-slate-800/40 rounded animate-pulse" />;
  }
  if (!data?.ok || !data.points || data.points.length < 2) {
    return <span className="text-slate-600 text-[10px]">—</span>;
  }

  const pts = data.points;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const W = 60;
  const H = 16;
  const path = pts
    .map((v, i) => {
      const x = (i / (pts.length - 1)) * (W - 2) + 1;
      const y = H - 1 - ((v - min) / range) * (H - 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const isUp = data.pct_change >= 0;
  const stroke = isUp ? "#10b981" : "#ef4444";

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={`${data.first_price} → ${data.last_price} (${data.days_since}d)`}
    >
      <svg width={W} height={H} className="overflow-visible">
        <path
          d={path}
          fill="none"
          stroke={stroke}
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className={`text-[10px] font-mono tabular-nums ${
          isUp ? "text-emerald-300" : "text-red-300"
        }`}
      >
        {isUp ? "+" : ""}
        {data.pct_change.toFixed(1)}%
      </span>
    </span>
  );
}
